import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { IoSearchOutline, IoClose } from "react-icons/io5";
import { IoCaretUp, IoCaretDown } from "react-icons/io5";
import { IoChevronBack, IoChevronForward } from "react-icons/io5";
import { Download } from "lucide-react";
import * as XLSX from "xlsx";
import useDebounce from "hooks/useDebounce";

// ─── Sort Icon ──────────────────────────────────────────────────────────────

const SortIcon = ({ direction }) => (
  <span className="inline-flex flex-col ml-1 align-middle" style={{ gap: 1 }}>
    <IoCaretUp
      size={8}
      className={direction === "asc" ? "text-black" : "text-mediumgray"}
    />
    <IoCaretDown
      size={8}
      className={direction === "desc" ? "text-black" : "text-mediumgray"}
    />
  </span>
);

// ─── Debounce hook ──────────────────────────────────────────────────────────

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * Safely read a nested value from an object using dot-notation key.
 * e.g. getNestedValue({ owner: { name: "Ali" } }, "owner.name") → "Ali"
 */
function getNestedValue(obj, key) {
  return key.split(".").reduce((acc, k) => acc?.[k], obj);
}

/**
 * Simple case-insensitive substring match.
 */
function matchesSearch(value, query) {
  if (value === null || value === undefined) return false;
  return String(value).toLowerCase().includes(query.toLowerCase().trim());
}

/**
 * Compare two values for sorting. Handles numbers, dates, and strings.
 */
function compareValues(a, b) {
  if (a === null || a === undefined) return 1;
  if (b === null || b === undefined) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  const da = Date.parse(a);
  const db = Date.parse(b);
  if (!isNaN(da) && !isNaN(db)) return da - db;
  return String(a).localeCompare(String(b));
}

/**
 * Build a plain array-of-objects export payload from columns + rows.
 * - Skips columns with exportable === false.
 * - Uses col.exportValue(row) when provided, otherwise the raw accessor value.
 * - Never calls col.cell() — that returns JSX, not data.
 */
function buildExportRows(columns, rows) {
  const exportCols = columns.filter((c) => c.exportable !== false);
  return rows.map((row) => {
    const out = {};
    for (const col of exportCols) {
      const header = col.header || col.accessorKey || "";
      let value;
      if (typeof col.exportValue === "function") {
        value = col.exportValue(row);
      } else {
        value = getNestedValue(row, col.accessorKey);
      }
      out[header] = value === null || value === undefined ? "" : value;
    }
    return out;
  });
}

/**
 * Generate and download an .xlsx file from columns + rows.
 */
function exportToXlsx(columns, rows, filename) {
  const exportRows = buildExportRows(columns, rows);
  const worksheet = XLSX.utils.json_to_sheet(exportRows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
  const safeName = (filename || "export").replace(/\.xlsx$/i, "");
  XLSX.writeFile(workbook, `${safeName}.xlsx`);
}

// ─── Pagination ─────────────────────────────────────────────────────────────

function PaginationBar({ currentPage, totalPages, onPageChanged }) {
  if (totalPages <= 1) return null;

  const range = (from, to) => {
    const arr = [];
    for (let i = from; i <= to; i++) arr.push(i);
    return arr;
  };

  const getPages = () => {
    const totalNumbers = 3;
    const totalBlocks = totalNumbers + 2;

    if (totalPages > totalBlocks) {
      const startPage = Math.max(2, currentPage - 1);
      const endPage = Math.min(totalPages - 1, currentPage + 1);
      let pages = range(startPage, endPage);

      const hasLeftSpill = startPage > 2;
      const hasRightSpill = totalPages - endPage > 1;
      const spillOffset = totalNumbers - (pages.length + 1);

      if (hasLeftSpill && !hasRightSpill) {
        const extraPages = range(startPage - spillOffset, startPage - 1);
        pages = [...extraPages, ...pages];
      }

      const result = [1];
      if (hasLeftSpill) result.push("...");
      result.push(...pages);
      if (hasRightSpill) result.push("...");
      result.push(totalPages);
      return result;
    }

    return range(1, totalPages);
  };

  const pages = getPages();

  const base =
    "relative flex items-center justify-center py-2.5 px-4 border border-darkgray bg-white text-darkergray text-sm transition-colors duration-150 -mr-px focus:outline-none";
  const hover = "hover:bg-lightgray hover:text-black";
  const activeStyle = "!bg-black !text-white !border-black";
  const disabledStyle = "opacity-40 cursor-not-allowed pointer-events-none";

  return (
    <nav aria-label="Pagination" className="mt-5 flex justify-center">
      <ul className="flex list-none p-0 m-0">
        {/* Prev */}
        <li>
          <button
            className={`${base} ${hover} rounded-l-md ${currentPage === 1 ? disabledStyle : ""} !px-2`}
            onClick={() => onPageChanged(currentPage - 1)}
            disabled={currentPage === 1}
            aria-label="Previous"
          >
            <IoChevronBack size={20} />
          </button>
        </li>

        {/* Page numbers */}
        {pages.map((page, i) =>
          page === "..." ? (
            <li key={`ellipsis-${i}`}>
              <span className={`${base} cursor-default`}>…</span>
            </li>
          ) : (
            <li key={page}>
              <button
                className={`${base} ${page === currentPage ? activeStyle : hover}`}
                onClick={() => onPageChanged(page)}
                aria-current={page === currentPage ? "page" : undefined}
              >
                {page}
              </button>
            </li>
          ),
        )}

        {/* Next */}
        <li>
          <button
            className={`${base} ${hover} rounded-r-md ${currentPage === totalPages ? disabledStyle : ""} !px-2`}
            onClick={() => onPageChanged(currentPage + 1)}
            disabled={currentPage === totalPages}
            aria-label="Next"
          >
            <IoChevronForward size={20} />
          </button>
        </li>
      </ul>
    </nav>
  );
}

// ─── Export Button ───────────────────────────────────────────────────────────

function ExportButton({ onClick, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="flex items-center gap-2 px-3.5 py-2 border border-mediumgray bg-white rounded-md text-sm text-darkergray hover:bg-lightgray hover:text-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
    >
      <Download size={15} />
      Export
    </button>
  );
}

// ─── Main Table Component ────────────────────────────────────────────────────
//
// Column definition shape:
// {
//   accessorKey : string              — dot-notation key e.g. "owner.name"
//   header      : string              — column heading text
//   cell?       : (row) => ReactNode  — optional custom cell renderer
//   isFilter?   : boolean             — include in global search (default: true)
//   isSortable? : boolean             — allow sorting (default: true)
//   minWidth?   : string              — optional min-width e.g. "200px"
//   maxWidth?   : string              — optional max-width e.g. "150px", truncates overflown text
//   wrap?       : boolean             — wrap long text to multiple lines instead of truncating (default: false)
//   stopPropagation?: boolean         — stop event propagation for row click (default: false)
//   exportable? : boolean             — include column in xlsx export (default: true)
//   exportValue?: (row) => any        — value to write to xlsx (default: raw accessor value).
//                                       Use this when the display cell is JSX/formatted but you
//                                       want a clean raw value in the file (e.g. a formatted date).
// }
//
// Props:
// {
//   columns        : Column[]
//   data           : object[]
//   title?         : string
//   pageSize?      : number          — rows per page (default: 10)
//   ButtonCmp?     : React component — rendered top-right next to search bar.
//                                      Receives { rows } = current filtered+sorted rows.
//   emptyText?     : string
//   onRowClick?    : (row) => void   — optional row click handler
//   exportable?    : boolean         — show a built-in Export (.xlsx) button (default: false)
//   exportFilename?: string          — base filename for the export (default: "export")
//   renderExpanded?: (row) => ReactNode — if set, rows are expandable; returns the
//                                         detail panel shown below an expanded row.
//   isRowExpandable?: (row) => boolean  — which rows can expand (default: all, when
//                                         renderExpanded is set).
//   searchable?    : boolean          — show the search input (default: true).
//                                       Ignored when serverPagination is set without
//                                       onSearchChange — see below.
//   searchPlaceholder?: string        — placeholder for the search input
//   defaultSearch? : string           — initial search text (e.g. hydrated from a URL param)
//   onSearchChange?: (query) => void  — receives the debounced, trimmed query. Set this to
//                                       search server-side: the parent puts the query in its
//                                       request and the table stops filtering locally.
//                                       REQUIRED to have a working search box alongside
//                                       serverPagination — with server pagination the table
//                                       only holds the current page, so a local filter would
//                                       search 50 rows out of 500 and look broken. The box is
//                                       hidden in that case rather than rendered dead.
//   serverPagination?: { page, totalPages, total, limit } — when set, pagination is
//                                         driven by the server; data is the current page only.
//   onPageChange?  : (page) => void   — required when serverPagination is set
//   pageSizeOptions?: number[]        — e.g. [10, 25, 50, 100]. Renders a rows-per-page
//                                       select in the footer. Client-side tables re-slice
//                                       on their own; server-paginated tables must also
//                                       handle onPageSizeChange.
//   onPageSizeChange?: (size) => void — called with the new page size. Required when
//                                       pageSizeOptions is set on a server-paginated table
//                                       (the parent owns the request), and the parent should
//                                       reset its page to 1.
// }

const SORT_CYCLE = { none: "asc", asc: "desc", desc: "none" };

// Column resizing
const MIN_COL_WIDTH = 60;
const DEFAULT_COL_MAX_WIDTH = "250px";
const colKeyOf = (col) => col.accessorKey || col.header;

export default function Table({
  columns = [],
  data = [],
  title = "",
  pageSize = 10,
  ButtonCmp = null,
  emptyText = "No data found",
  onRowClick = null,
  // (row) => string — extra classes per row, for marking rows that need
  // attention without a separate list of exceptions beside the table.
  rowClassName = null,
  exportable = false,
  exportFilename = "export",
  renderExpanded = null,
  isRowExpandable = null,
  searchable = true,
  searchPlaceholder = "Search...",
  defaultSearch = "",
  onSearchChange = null,
  serverPagination = null,
  onPageChange = null,
  pageSizeOptions = null,
  onPageSizeChange = null,
}) {
  const [searchQuery, setSearchQuery] = useState(defaultSearch);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("none");
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedKey, setExpandedKey] = useState(null);
  // Effective rows-per-page. Seeded from the pageSize prop and changed by the
  // footer select; the prop stays authoritative if the parent updates it.
  const [selectedPageSize, setSelectedPageSize] = useState(pageSize);

  // Temporary (session-only) column widths set by dragging the header handle
  const [colWidths, setColWidths] = useState({});
  const [resizingKey, setResizingKey] = useState(null);
  const resizeRef = useRef(null);
  const didResizeRef = useRef(false);

  const debouncedQuery = useDebounce(searchQuery, 300);
  const isServerPaginated = !!serverPagination;
  const isServerSearch = !!onSearchChange;

  // A search box the table can't act on is worse than none: with server
  // pagination the rows in hand are one page, so local filtering silently
  // searches a fraction of the data. Require onSearchChange to opt in.
  const showSearch = searchable && (!isServerPaginated || isServerSearch);

  useEffect(() => {
    setSelectedPageSize(pageSize);
  }, [pageSize]);

  // Hand the settled query to the parent so it can put it in the request.
  // Kept in a ref so an inline arrow prop doesn't re-fire the effect, and
  // skipped on mount so a parent-supplied defaultSearch isn't cleared.
  const searchChangeRef = useRef(onSearchChange);
  useEffect(() => {
    searchChangeRef.current = onSearchChange;
  }, [onSearchChange]);

  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    searchChangeRef.current?.(debouncedQuery.trim());
  }, [debouncedQuery]);

  // ── Columns that participate in search ──────────────────────────────────
  const filterableKeys = useMemo(
    () =>
      columns
        .filter((col) => col.isFilter !== false)
        .map((col) => col.accessorKey)
        .filter(Boolean),
    [columns],
  );

  // ── 1. Filter ────────────────────────────────────────────────────────────
  const filteredData = useMemo(() => {
    // When the parent owns the search (server-side), the rows it hands back are
    // already the result — filtering them again would double-filter.
    if (isServerPaginated || isServerSearch || !debouncedQuery.trim())
      return data;
    return data.filter((row) =>
      filterableKeys.some((key) =>
        matchesSearch(getNestedValue(row, key), debouncedQuery),
      ),
    );
  }, [data, debouncedQuery, filterableKeys, isServerPaginated, isServerSearch]);

  // ── 2. Sort ──────────────────────────────────────────────────────────────
  const sortedData = useMemo(() => {
    if (!sortKey || sortDir === "none") return filteredData;
    return [...filteredData].sort((a, b) => {
      const cmp = compareValues(
        getNestedValue(a, sortKey),
        getNestedValue(b, sortKey),
      );
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [filteredData, sortKey, sortDir]);

  // ── 3. Paginate ──────────────────────────────────────────────────────────
  const clientTotalPages = Math.max(
    1,
    Math.ceil(sortedData.length / selectedPageSize),
  );
  const totalPages = isServerPaginated
    ? Math.max(1, serverPagination.totalPages || 1)
    : clientTotalPages;
  const activePage = isServerPaginated
    ? serverPagination.page || 1
    : currentPage;

  // Reset to page 1 when search changes (client-side only)
  useEffect(() => {
    if (isServerPaginated) return;
    setCurrentPage(1);
    setExpandedKey(null);
  }, [debouncedQuery, isServerPaginated]);

  // Collapse any expanded row when the page changes
  useEffect(() => {
    setExpandedKey(null);
  }, [currentPage]);

  // Clamp page if data shrinks (client-side only)
  useEffect(() => {
    if (isServerPaginated) return;
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalPages, currentPage, isServerPaginated]);

  const pagedData = useMemo(() => {
    if (isServerPaginated) return sortedData;
    const start = (currentPage - 1) * selectedPageSize;
    return sortedData.slice(start, start + selectedPageSize);
  }, [sortedData, currentPage, selectedPageSize, isServerPaginated]);

  const handlePageChanged = useCallback(
    (page) => {
      if (isServerPaginated) {
        onPageChange?.(page);
      } else {
        setCurrentPage(page);
      }
    },
    [isServerPaginated, onPageChange],
  );

  // Changing rows-per-page always returns to page 1 — the old page number
  // usually points past the end of the resized result set.
  const handlePageSizeChanged = useCallback(
    (size) => {
      setSelectedPageSize(size);
      setCurrentPage(1);
      setExpandedKey(null);
      onPageSizeChange?.(size);
    },
    [onPageSizeChange],
  );

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleSort = useCallback(
    (col) => {
      if (col.isSortable === false) return;
      if (sortKey !== col.accessorKey) {
        setSortKey(col.accessorKey);
        setSortDir("asc");
      } else {
        const next = SORT_CYCLE[sortDir];
        setSortDir(next);
        if (next === "none") setSortKey(null);
      }
    },
    [sortKey, sortDir],
  );

  const handleSearch = useCallback((e) => {
    setSearchQuery(e.target.value);
  }, []);

  // ── Column resizing ──────────────────────────────────────────────────────
  // Drag the handle on a header's right edge to widen a column and read the
  // full cell contents. Double-click the handle to snap it back.
  const startResize = useCallback((e, col) => {
    e.preventDefault();
    e.stopPropagation();
    const th = e.currentTarget.closest("th");
    if (!th) return;
    const key = colKeyOf(col);
    resizeRef.current = {
      key,
      startX: e.clientX,
      startWidth: th.getBoundingClientRect().width,
    };
    didResizeRef.current = false;
    setResizingKey(key);
  }, []);

  const resetColWidth = useCallback((e, col) => {
    e.preventDefault();
    e.stopPropagation();
    const key = colKeyOf(col);
    setColWidths((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  useEffect(() => {
    const handleMove = (e) => {
      const drag = resizeRef.current;
      if (!drag) return;
      const delta = e.clientX - drag.startX;
      if (Math.abs(delta) > 2) didResizeRef.current = true;
      const width = Math.max(MIN_COL_WIDTH, Math.round(drag.startWidth + delta));
      setColWidths((prev) =>
        prev[drag.key] === width ? prev : { ...prev, [drag.key]: width },
      );
    };

    const handleUp = () => {
      if (!resizeRef.current) return;
      resizeRef.current = null;
      setResizingKey(null);
      // Cleared after the click that follows mouseup, so the drag doesn't sort.
      setTimeout(() => {
        didResizeRef.current = false;
      }, 0);
    };

    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
  }, []);

  // Suppress text selection page-wide while dragging
  useEffect(() => {
    if (!resizingKey) return;
    const { body } = document;
    const prevCursor = body.style.cursor;
    const prevSelect = body.style.userSelect;
    body.style.cursor = "col-resize";
    body.style.userSelect = "none";
    return () => {
      body.style.cursor = prevCursor;
      body.style.userSelect = prevSelect;
    };
  }, [resizingKey]);

  const handleHeaderClick = useCallback(
    (col) => {
      if (didResizeRef.current) return; // ignore the click that ends a drag
      handleSort(col);
    },
    [handleSort],
  );

  // Export the current filtered + sorted rows (all pages, not just the visible one)
  const handleExport = useCallback(() => {
    if (sortedData.length === 0) return;
    exportToXlsx(columns, sortedData, exportFilename);
  }, [columns, sortedData, exportFilename]);

  // ── Render ────────────────────────────────────────────────────────────────
  const resultTotal = isServerPaginated
    ? serverPagination.total || 0
    : sortedData.length;
  const pageLimit = isServerPaginated
    ? serverPagination.limit || selectedPageSize
    : selectedPageSize;
  const showingFrom =
    resultTotal === 0 ? 0 : (activePage - 1) * pageLimit + 1;
  const showingTo = isServerPaginated
    ? Math.min(activePage * pageLimit, resultTotal)
    : Math.min(activePage * selectedPageSize, sortedData.length);
  const showPageSizeSelect =
    Array.isArray(pageSizeOptions) && pageSizeOptions.length > 0;

  return (
    <div className="w-full">
      {title && <h4>{title}</h4>}

      {/* ── Toolbar ── */}
      <div className="flex items-center justify-between mb-5 gap-3">
        {/* Search */}
        {showSearch ? (
          <div className="flex items-center gap-2 border border-mediumgray bg-white rounded-md px-3 py-2 w-full max-w-sm focus-within:border-darkgray transition-colors">
            <IoSearchOutline
              size={16}
              className="text-darkgray flex-shrink-0"
            />
            <input
              type="text"
              value={searchQuery}
              onChange={handleSearch}
              placeholder={searchPlaceholder}
              className="flex-1 bg-transparent outline-none text-sm text-black placeholder-darkgray"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                aria-label="Clear search"
                className="text-darkgray hover:text-black flex-shrink-0"
              >
                <IoClose size={16} />
              </button>
            )}
          </div>
        ) : (
          <div />
        )}

        {/* Actions */}
        <div className="flex items-center gap-2 flex-shrink-0 ml-auto">
          {exportable && (
            <ExportButton
              onClick={handleExport}
              disabled={sortedData.length === 0}
            />
          )}
          {ButtonCmp && <ButtonCmp rows={sortedData} />}
        </div>
      </div>

      {/* ── Table ── */}
      <div className="w-full overflow-x-auto">
        <table className="w-full border-collapse text-sm border border-mediumgray">
          <thead>
            <tr className="border-b border-mediumgray bg-lightgray">
              {columns.map((col) => {
                const isSortable = col.isSortable !== false;
                const isActive = sortKey === col.accessorKey;
                const direction = isActive ? sortDir : "none";
                const key = colKeyOf(col);
                const resized = colWidths[key];
                const isResizing = resizingKey === key;
                const style = resized
                  ? {
                      width: resized,
                      minWidth: resized,
                      maxWidth: resized,
                    }
                  : {
                      minWidth: col.minWidth,
                      maxWidth: col.maxWidth || DEFAULT_COL_MAX_WIDTH,
                    };

                return (
                  <th
                    key={key}
                    onClick={() => handleHeaderClick(col)}
                    style={style}
                    className={[
                      "group relative px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-darkergray select-none",
                      isSortable ? "cursor-pointer hover:text-black" : "",
                      col.maxWidth || resized ? "truncate" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    <span className="inline-flex items-center">
                      {col.header}
                      {isSortable && <SortIcon direction={direction} />}
                    </span>

                    {/* Resize handle — appears on hover, drag to widen */}
                    <span
                      role="separator"
                      aria-orientation="vertical"
                      aria-label={`Resize ${col.header || ""} column`}
                      title="Drag to resize · double-click to reset"
                      onMouseDown={(e) => startResize(e, col)}
                      onDoubleClick={(e) => resetColWidth(e, col)}
                      onClick={(e) => e.stopPropagation()}
                      className={[
                        "absolute top-0 right-0 z-10 flex h-full w-2 cursor-col-resize items-center justify-center transition-opacity",
                        isResizing
                          ? "opacity-100"
                          : "opacity-0 group-hover:opacity-100",
                      ].join(" ")}
                    >
                      <span
                        className={[
                          "h-1/2 w-0.5 rounded-full",
                          isResizing ? "bg-black" : "bg-darkgray",
                        ].join(" ")}
                      />
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {pagedData.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-5 py-12 text-center text-darkgray text-sm"
                >
                  {emptyText}
                </td>
              </tr>
            ) : (
              pagedData.map((row, rowIdx) => {
                const rowKey = `${activePage}-${rowIdx}`;
                const canExpand =
                  !!renderExpanded &&
                  (isRowExpandable ? isRowExpandable(row) : true);
                const isExpanded = canExpand && expandedKey === rowKey;
                const clickable = !!onRowClick || canExpand;

                const handleRowClick = () => {
                  if (canExpand) {
                    setExpandedKey(isExpanded ? null : rowKey);
                  }
                  onRowClick?.(row);
                };

                return (
                  <React.Fragment key={rowKey}>
                    <tr
                      onClick={handleRowClick}
                      className={[
                        "border-b border-mediumgray transition-colors",
                        clickable
                          ? "cursor-pointer hover:bg-lightgray"
                          : "hover:bg-lightergray",
                        isExpanded ? "bg-lightgray" : "",
                        rowClassName?.(row) || "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                    >
                      {columns.map((col, colIdx) => {
                        const value = getNestedValue(row, col.accessorKey);
                        const resized = colWidths[colKeyOf(col)];
                        const isWrap = col.wrap === true;
                        const titleAttr =
                          !isWrap && value != null && typeof value !== "object"
                            ? String(value)
                            : undefined;
                        return (
                          <td
                            key={col.accessorKey || col.header}
                            title={titleAttr}
                            className={[
                              "px-5 py-4 text-darkergray align-top",
                              isWrap
                                ? "whitespace-normal break-words"
                                : "truncate",
                            ].join(" ")}
                            style={
                              resized
                                ? {
                                    width: resized,
                                    minWidth: resized,
                                    maxWidth: resized,
                                  }
                                : {
                                    maxWidth:
                                      col.maxWidth || DEFAULT_COL_MAX_WIDTH,
                                  }
                            }
                            onClick={
                              col?.stopPropagation
                                ? (e) => e.stopPropagation()
                                : undefined
                            }
                          >
                            <span className="inline-flex items-start gap-1.5">
                              {colIdx === 0 && canExpand && (
                                <IoChevronForward
                                  size={14}
                                  className={[
                                    "mt-0.5 flex-shrink-0 text-darkgray transition-transform",
                                    isExpanded ? "rotate-90" : "",
                                  ].join(" ")}
                                />
                              )}
                              <span>
                                {col.cell
                                  ? col.cell(row, rowIdx, activePage)
                                  : (value ?? "-")}
                              </span>
                            </span>
                          </td>
                        );
                      })}
                    </tr>
                    {isExpanded && (
                      <tr className="border-b border-mediumgray bg-lightergray">
                        <td colSpan={columns.length} className="px-5 py-4">
                          {renderExpanded(row)}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* ── Footer: count + pagination ── */}
      {(resultTotal > 0 || isServerPaginated) && (
        <div className="mt-4 flex flex-col items-center gap-1">
          <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
            {resultTotal > 0 && (
              <p className="text-xs text-darkgray">
                Showing {showingFrom}–{showingTo} of {resultTotal} results
              </p>
            )}
            {showPageSizeSelect && (
              <label className="flex items-center gap-1.5 text-xs text-darkgray">
                Rows per page
                <select
                  value={selectedPageSize}
                  onChange={(e) =>
                    handlePageSizeChanged(Number(e.target.value))
                  }
                  className="form-select border border-mediumgray !w-auto !min-h-0 !py-1 !pl-2 !pr-7 !text-xs text-black"
                >
                  {pageSizeOptions.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
          <PaginationBar
            currentPage={activePage}
            totalPages={totalPages}
            onPageChanged={handlePageChanged}
          />
        </div>
      )}
    </div>
  );
}

// Exported so a parent (e.g. an "Export All" multi-sheet button) can reuse
// the exact same column-aware row-building logic.
export { buildExportRows, exportToXlsx };
